import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faTimesCircle } from "@fortawesome/free-solid-svg-icons"
import {
	ENTRETIEN_CYCLE_JOURNALIER,
	ENTRETIEN_CYCLE_UNIQUE,
	fetchEntretiensSemaine,
	getBaseEntretien,
	getBaseEntretienCycle,
	postEntretien,
} from "../../store/actions/entretienActions"
import * as Addons from "../../utilities/Addons"
import { useGroups } from "../../hooks/groups"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import CustomModal from "../basics/interface/CustomModal"
import InputV2 from "../basics/input/InputV2"
import { filterEmployesActifs } from "../../utilities/employes"

const PORTEE_FLOTTE = 0
const PORTEE_TYPE = 1
const PORTEE_VEHICULE = 2

const toggleIn = (list, value) =>
	list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

// La date du cycle circule en ISO (yyyy-mm-dd) pour l'<input type="date">. Le
// serializer renvoie dd-mm-yyyy (affichage) → conversion au préremplissage.
const isISO = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d || "")
const frToISO = (d) => {
	if (!d) return ""
	const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(d)
	if (m) return `${m[3]}-${m[2]}-${m[1]}`
	return isISO(d) ? d : ""
}

// Contrôles toujours pleine largeur de leur colonne (gs.input/select ont flex:1,
// inopérant hors conteneur flex → on force width:100% pour éviter tout débordement).
const inp = { ...gs.input, width: "100%" }
const sel = { ...gs.select, width: "100%" }

const styles = {
	field: { marginBottom: 14 },
	label: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		display: "block",
		marginBottom: 6,
	},
	checkList: {
		maxHeight: 160,
		overflowY: "auto",
		border: `1px solid ${COLORS.border}`,
		borderRadius: 8,
		padding: 8,
	},
	checkRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		padding: "3px 2px",
	},
	grid2: {
		display: "grid",
		gridTemplateColumns: "1fr 120px",
		gap: 12,
		alignItems: "end",
	},
	grid11: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
	box: (active) => ({
		border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
		borderRadius: 10,
		padding: 14,
		marginBottom: 14,
		backgroundColor: active ? COLORS.accentLight : COLORS.surface,
	}),
	boxHead: {
		display: "flex",
		alignItems: "center",
		gap: 9,
		fontWeight: 600,
		fontSize: 14,
		cursor: "pointer",
		userSelect: "none",
	},
	boxBody: { marginTop: 12 },
	erreur: {
		marginTop: 10,
		fontSize: 13,
		color: COLORS.danger,
		textAlign: "center",
	},
	hint: {
		fontSize: 12.5,
		color: COLORS.textMuted,
		backgroundColor: COLORS.bg,
		borderRadius: 10,
		padding: "10px 12px",
		marginBottom: 14,
		lineHeight: 1.5,
	},
}

const Field = ({ label, children, style }) => (
	<div style={{ ...styles.field, ...(style || {}) }}>
		{label && <label style={styles.label}>{label}</label>}
		{children}
	</div>
)

const EntretienAddEditModal = ({ closeModal, ent = null }) => {
	const dispatch = useDispatch()
	const { postEntretien: postState } = useSelector(
		(state) => state.entretienInfo,
	)
	const { employes: depanneurs, masterVehicules } = useSelector(
		(state) => state.masterReducer,
	)
	const { listeAtelierModeles } = useSelector(
		(state) => state.atelierModeleInfo,
	)
	const { isBoss } = useGroups()

	const [entretien, setEntretien] = useState(() => {
		const base = ent ?? getBaseEntretien()
		return {
			...base,
			cycle_number: base.cycle_number == null ? "" : base.cycle_number,
			cycle_km: base.cycle_km == null ? "" : base.cycle_km,
		}
	})
	const [cycle, setCycle] = useState(() => {
		if (ent && ent.prochain_entretien) {
			return {
				...getBaseEntretienCycle(),
				date: frToISO(ent.prochain_entretien.date),
				km_cible:
					ent.prochain_entretien.km_cible == null
						? ""
						: ent.prochain_entretien.km_cible,
			}
		}
		return getBaseEntretienCycle()
	})
	const [dateActive, setDateActive] = useState(
		ent
			? ent.cycle_type === ENTRETIEN_CYCLE_UNIQUE ||
					(ent.cycle_number != null && ent.cycle_number !== "")
			: true,
	)
	const [kmActive, setKmActive] = useState(
		ent ? ent.cycle_km != null && ent.cycle_km !== "" : false,
	)

	const update = (key, value) => setEntretien((e) => ({ ...e, [key]: value }))
	const updateCycle = (key, value) =>
		setCycle((c) => ({ ...c, [key]: value }))

	const typeUnique = entretien.cycle_type === ENTRETIEN_CYCLE_UNIQUE

	const masterModeles = useMemo(
		() => listeAtelierModeles.filter((a) => a.svp),
		[listeAtelierModeles],
	)
	const vehiculeParties = useMemo(() => {
		if (
			entretien.portee !== PORTEE_VEHICULE ||
			(entretien.portee_vehicules || []).length !== 1
		)
			return []
		const v = masterVehicules.find(
			(mv) => mv.id === entretien.portee_vehicules[0],
		)
		return v && v.propre_vehicule ? v.propre_vehicule.parties : []
	}, [masterVehicules, entretien.portee, entretien.portee_vehicules])

	useEffect(() => {
		if (postState.saved) closeModal()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postState.saved])

	// Bouton déjà dans un modal : switch « Enregistrer » → « Confirmer ? »
	// (src/CLAUDE.md), annulé dès que la saisie change ou que le save échoue.
	const [confirm, setConfirm] = useState(false)
	useEffect(() => {
		setConfirm(false)
	}, [entretien, cycle, dateActive, kmActive])
	useEffect(() => {
		if (postState.error) setConfirm(false)
	}, [postState.error])

	const saveEntretien = () => {
		if (!confirm) {
			setConfirm(true)
			return
		}
		const payloadEntretien = {
			...entretien,
			cycle_type: dateActive
				? entretien.cycle_type
				: ENTRETIEN_CYCLE_JOURNALIER,
			cycle_number:
				dateActive && !typeUnique ? entretien.cycle_number : "",
			cycle_km: kmActive && !typeUnique ? entretien.cycle_km : "",
		}
		const payloadCycle = {
			...cycle,
			date: dateActive ? cycle.date : "",
			km_cible: kmActive && !typeUnique ? cycle.km_cible : "",
			// Édition : seul le cycle de ce véhicule reprend date/km (B3).
			vehicule_id: ent ? ent.vehicule_contexte || 0 : 0,
		}
		dispatch(
			postEntretien(
				JSON.stringify({
					entretien: payloadEntretien,
					entretienCycle: payloadCycle,
				}),
			),
		).then(() => dispatch(fetchEntretiensSemaine()))
	}

	const isDisabled = () => {
		const nomInvalide = entretien.nom === "" && entretien.modele === 0
		const porteeInvalide =
			(entretien.portee === PORTEE_TYPE &&
				(entretien.portee_types || []).length === 0) ||
			(entretien.portee === PORTEE_VEHICULE &&
				(entretien.portee_vehicules || []).length === 0)
		let echeanceInvalide
		if (typeUnique) {
			// Édition : date facultative (plan déjà fait — renommer ne le
			// rouvre pas) ; le serveur l'exige si une échéance doit la prendre.
			echeanceInvalide =
				ent && ent.id
					? cycle.date !== "" && !isISO(cycle.date)
					: !isISO(cycle.date)
		} else {
			const dateOk =
				dateActive && entretien.cycle_number !== "" && isISO(cycle.date)
			const kmOk =
				kmActive && entretien.cycle_km !== "" && cycle.km_cible !== ""
			const dateBroken =
				dateActive &&
				!(entretien.cycle_number !== "" && isISO(cycle.date))
			const kmBroken =
				kmActive &&
				!(entretien.cycle_km !== "" && cycle.km_cible !== "")
			echeanceInvalide = (!dateOk && !kmOk) || dateBroken || kmBroken
		}
		return nomInvalide || porteeInvalide || echeanceInvalide
	}

	const dateLabel = typeUnique
		? "Date d'entretien"
		: "Date du prochain entretien"

	return (
		<CustomModal
			title={ent && ent.id ? "Éditer l'entretien" : "Nouvel entretien"}
			onClose={postState.saving ? () => {} : closeModal}
			showCloseButton={!postState.saving}
			maxWidth={560}
			buttons={[
				{
					title: "Annuler",
					styleName: "secondary",
					onClick: closeModal,
					disabled: postState.saving,
				},
				{
					title: postState.saving ? (
						<FontAwesomeIcon icon={faSync} spin />
					) : confirm ? (
						"Confirmer ?"
					) : (
						"Enregistrer"
					),
					styleName: "primary",
					onClick: saveEntretien,
					disabled: postState.saving || isDisabled(),
				},
			]}
		>
			<Field label="Nom">
				<input
					style={inp}
					type="text"
					value={entretien.nom}
					onChange={(e) => update("nom", e.target.value)}
				/>
			</Field>

			<Field label="Appliquer à">
				<select
					style={sel}
					value={entretien.portee}
					onChange={(e) => update("portee", parseInt(e.target.value))}
				>
					<option value={PORTEE_FLOTTE}>Toute la flotte</option>
					<option value={PORTEE_TYPE}>Par type de véhicule</option>
					<option value={PORTEE_VEHICULE}>Par véhicule</option>
				</select>
			</Field>

			{entretien.portee === PORTEE_TYPE && (
				<Field label="Types concernés">
					<div style={styles.checkList}>
						{Addons.PROPRE_VEHICULE_TYPE_CHOICES.map(
							([val, lib]) => (
								<label
									key={`pt_${val}`}
									style={styles.checkRow}
								>
									<input
										type="checkbox"
										checked={(
											entretien.portee_types || []
										).includes(val)}
										onChange={() =>
											update(
												"portee_types",
												toggleIn(
													entretien.portee_types ||
														[],
													val,
												),
											)
										}
									/>
									{lib}
								</label>
							),
						)}
					</div>
				</Field>
			)}

			{entretien.portee === PORTEE_VEHICULE && (
				<Field label="Véhicules concernés">
					<div style={styles.checkList}>
						{[...masterVehicules]
							.sort((a, b) =>
								`${a.marque} ${a.modele}`.localeCompare(
									`${b.marque} ${b.modele}`,
								),
							)
							.map((veh) => (
								<label
									key={`pv_${veh.id}`}
									style={styles.checkRow}
								>
									<input
										type="checkbox"
										checked={(
											entretien.portee_vehicules || []
										).includes(veh.id)}
										onChange={() =>
											update(
												"portee_vehicules",
												toggleIn(
													entretien.portee_vehicules ||
														[],
													veh.id,
												),
											)
										}
									/>
									{`${veh.marque} ${veh.modele}`}
								</label>
							))}
					</div>
				</Field>
			)}

			{vehiculeParties.length > 0 && (
				<Field label="Partie">
					<select
						style={sel}
						value={entretien.partie}
						onChange={(e) =>
							update("partie", parseInt(e.target.value))
						}
					>
						<option value={0}>-</option>
						{vehiculeParties.map((part) => (
							<option key={`part${part.id}`} value={part.id}>
								{part.label}
							</option>
						))}
					</select>
				</Field>
			)}

			<Field label="Modèle d'atelier">
				<select
					style={sel}
					value={entretien.modele}
					onChange={(e) => update("modele", parseInt(e.target.value))}
				>
					<option value={0}>-</option>
					{masterModeles.map((modele) => (
						<option key={`modele${modele.id}`} value={modele.id}>
							{modele.nom}
						</option>
					))}
				</select>
			</Field>

			{ent && ent.id && (
				<div style={styles.hint}>
					{ent.vehicule_contexte
						? `Date et seuil km ne s'appliquent qu'à l'échéance de ${
								ent.vehicule_contexte_label || "ce véhicule"
							} (et aux véhicules nouvellement couverts) ; les autres véhicules gardent les leurs.`
						: "Date et seuil km ne s'appliquent qu'aux véhicules nouvellement couverts ; pour une échéance existante, utiliser « Décaler » sur sa ligne."}
				</div>
			)}

			{/* Échéance par date */}
			<div style={styles.box(dateActive)}>
				<label style={styles.boxHead}>
					<input
						type="checkbox"
						checked={dateActive}
						onChange={() => setDateActive(!dateActive)}
					/>
					Échéance par date
				</label>
				{dateActive && (
					<div style={styles.boxBody}>
						<div style={typeUnique ? undefined : styles.grid2}>
							<Field label="Cycle" style={{ marginBottom: 0 }}>
								<select
									style={sel}
									value={entretien.cycle_type}
									onChange={(e) =>
										update(
											"cycle_type",
											parseInt(e.target.value),
										)
									}
								>
									<option value={0}>Journalier</option>
									<option value={1}>Mensuel</option>
									<option value={2}>Unique</option>
								</select>
							</Field>
							{!typeUnique && (
								<Field
									label={
										entretien.cycle_type ===
										ENTRETIEN_CYCLE_JOURNALIER
											? "Jours"
											: "Mois"
									}
									style={{ marginBottom: 0 }}
								>
									<input
										style={inp}
										type="text"
										value={entretien.cycle_number}
										onChange={(e) =>
											update(
												"cycle_number",
												e.target.value,
											)
										}
										onKeyDown={(e) => Addons.typeNumber(e)}
									/>
								</Field>
							)}
						</div>
						<Field
							label={dateLabel}
							style={{ marginBottom: 0, marginTop: 12 }}
						>
							<InputV2
								type="date"
								style={inp}
								value={cycle.date}
								onChange={(e) =>
									updateCycle("date", e.target.value)
								}
							/>
						</Field>
					</div>
				)}
			</div>

			{/* Échéance par kilométrage (incompatible avec le type Unique) */}
			{!typeUnique && (
				<div style={styles.box(kmActive)}>
					<label style={styles.boxHead}>
						<input
							type="checkbox"
							checked={kmActive}
							onChange={() => setKmActive(!kmActive)}
						/>
						Échéance par kilométrage
					</label>
					{kmActive && (
						<div style={{ ...styles.boxBody, ...styles.grid11 }}>
							<Field
								label="Tous les (km)"
								style={{ marginBottom: 0 }}
							>
								<input
									style={inp}
									type="text"
									value={entretien.cycle_km}
									onChange={(e) =>
										update("cycle_km", e.target.value)
									}
									onKeyDown={(e) => Addons.typeNumber(e)}
								/>
							</Field>
							<Field
								label="Prochain seuil (km)"
								style={{ marginBottom: 0 }}
							>
								<input
									style={inp}
									type="text"
									value={cycle.km_cible}
									onChange={(e) =>
										updateCycle("km_cible", e.target.value)
									}
									onKeyDown={(e) => Addons.typeNumber(e)}
								/>
							</Field>
						</div>
					)}
				</div>
			)}

			{isBoss && typeUnique && (
				<div style={styles.box(entretien.is_erreur_employe)}>
					<label style={styles.boxHead}>
						<input
							type="checkbox"
							checked={entretien.is_erreur_employe}
							onChange={() =>
								update(
									"is_erreur_employe",
									!entretien.is_erreur_employe,
								)
							}
						/>
						Erreur d'un employé
					</label>
					{entretien.is_erreur_employe && (
						<div style={styles.boxBody}>
							<Field label="Employé">
								<select
									style={sel}
									value={entretien.employe_erreur}
									onChange={(e) =>
										update(
											"employe_erreur",
											parseInt(e.target.value),
										)
									}
								>
									<option value={0}>-</option>
									{filterEmployesActifs(
										depanneurs,
										entretien.employe_erreur,
									).map((dep) => (
										<option
											key={`dep_${dep.id}`}
											value={dep.id}
										>
											{dep.nom}
										</option>
									))}
								</select>
							</Field>
							<Field
								label="Description"
								style={{ marginBottom: 0 }}
							>
								<textarea
									style={{
										...inp,
										minHeight: 80,
										resize: "vertical",
									}}
									rows={4}
									value={entretien.erreur_texte}
									onChange={(e) =>
										update("erreur_texte", e.target.value)
									}
								/>
							</Field>
						</div>
					)}
				</div>
			)}
			{/* postEntretien est `silent` : l'erreur serveur s'affiche ici. */}
			{postState.error && (
				<div style={styles.erreur}>
					<FontAwesomeIcon icon={faTimesCircle} />{" "}
					{postState.error.message || "Une erreur est survenue."}
				</div>
			)}
		</CustomModal>
	)
}

export default EntretienAddEditModal
